import React, { useEffect, useState } from 'react';
import { useMaestroContext } from '../context/MaestroContext';
import maestroService from '../maestroService';
import { ConfiguracaoCanais, EntregaNotificacao, RegraNotificacao, TemplateNotificacao } from '../models/notificacoes';
import { dispararAcaoSimulada } from '../utils/acaoSimulada';
import { formatarData } from '../utils/formatters';
import { EVENTOS_NOTIFICACAO } from '../utils/labels';
import Card from '../components/Card';
import EstadoVazio from '../components/EstadoVazio';
import Header from '../components/Header';
import { SkeletonCard } from '../components/Skeletons';

type AbaNotificacoes = 'REGRAS' | 'TEMPLATES' | 'CANAIS' | 'TESTAR' | 'ENTREGAS';

const CANAIS = ['MAESTRO', 'EMAIL', 'WHATSAPP'];
const STATUS_ENTREGA = ['ENVIADA', 'FALHA', 'PENDENTE'];

const labelEvento = (valor: string) => EVENTOS_NOTIFICACAO.find(e => e.valor === valor)?.label ?? valor;

const regraVazia = (eventType: string): { eventType: string; canalMaestro: boolean; canalEmail: boolean; canalWhatsapp: boolean; antecedenciaDiasUteis: string; destinatarios: string; alertaInternoObrigatorio: boolean; ativo: boolean } => ({
  eventType, canalMaestro: true, canalEmail: false, canalWhatsapp: false, antecedenciaDiasUteis: '', destinatarios: '', alertaInternoObrigatorio: false, ativo: true,
});

// MAESTRO-RN-43 — Central de Notificações: regras por evento, templates versionados por canal,
// configuração de canais (e-mail/WhatsApp), envio de teste e a fila real de entregas.
const MaestroNotificacoesPage: React.FC = () => {
  const { usuarios, cicloAtivo } = useMaestroContext();
  const [aba, setAba] = useState<AbaNotificacoes>('REGRAS');
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [regras, setRegras] = useState<RegraNotificacao[]>([]);
  const [editandoRegra, setEditandoRegra] = useState(regraVazia(EVENTOS_NOTIFICACAO[0].valor));
  const [salvandoRegra, setSalvandoRegra] = useState(false);

  const [eventoTemplate, setEventoTemplate] = useState(EVENTOS_NOTIFICACAO[0].valor);
  const [canalTemplate, setCanalTemplate] = useState(CANAIS[0]);
  const [templates, setTemplates] = useState<TemplateNotificacao[]>([]);
  const [carregandoTemplates, setCarregandoTemplates] = useState(false);
  const [novoTemplate, setNovoTemplate] = useState({ assunto: '', corpo: '' });
  const [mostrarNovoTemplate, setMostrarNovoTemplate] = useState(false);

  const [canais, setCanais] = useState<ConfiguracaoCanais | null>(null);
  const [formCanais, setFormCanais] = useState({ emailHabilitado: true, whatsappStatus: 'DESABILITADO', whatsappNumeroRemetente: '', whatsappTokenConfigurado: false });
  const [salvandoCanais, setSalvandoCanais] = useState(false);

  const [teste, setTeste] = useState({ eventType: EVENTOS_NOTIFICACAO[0].valor, canal: CANAIS[0], destinatarioUsuarioId: '' });
  const [resultadoTeste, setResultadoTeste] = useState<EntregaNotificacao[] | null>(null);
  const [enviandoTeste, setEnviandoTeste] = useState(false);

  const [entregas, setEntregas] = useState<EntregaNotificacao[]>([]);
  const [statusEntrega, setStatusEntrega] = useState('');
  const [carregandoEntregas, setCarregandoEntregas] = useState(false);

  useEffect(() => {
    Promise.all([maestroService.listarRegrasNotificacao(), maestroService.obterConfiguracaoCanais()])
      .then(([r, c]) => {
        setRegras(r);
        setCanais(c);
        setFormCanais({ emailHabilitado: c.emailHabilitado, whatsappStatus: c.whatsappStatus, whatsappNumeroRemetente: c.whatsappNumeroRemetente ?? '', whatsappTokenConfigurado: c.whatsappTokenConfigurado });
      })
      .catch(e => setErro(e?.response?.data?.message ?? 'Não foi possível carregar a configuração de notificações.'))
      .finally(() => setCarregando(false));
  }, []);

  useEffect(() => {
    if (aba !== 'TEMPLATES') return;
    setCarregandoTemplates(true);
    maestroService.listarTemplatesNotificacao(eventoTemplate, canalTemplate)
      .then(setTemplates)
      .catch(() => setTemplates([]))
      .finally(() => setCarregandoTemplates(false));
  }, [aba, eventoTemplate, canalTemplate]);

  useEffect(() => {
    if (aba !== 'ENTREGAS') return;
    setCarregandoEntregas(true);
    maestroService.listarEntregasNotificacao(cicloAtivo.id || undefined, statusEntrega || undefined)
      .then(dados => setEntregas(dados.sort((a, b) => b.criadoEm.localeCompare(a.criadoEm))))
      .catch(() => setEntregas([]))
      .finally(() => setCarregandoEntregas(false));
  }, [aba, statusEntrega, cicloAtivo.id]);

  const editarRegraExistente = (r: RegraNotificacao) => setEditandoRegra({
    eventType: r.eventType, canalMaestro: r.canalMaestro, canalEmail: r.canalEmail, canalWhatsapp: r.canalWhatsapp,
    antecedenciaDiasUteis: r.antecedenciaDiasUteis != null ? String(r.antecedenciaDiasUteis) : '',
    destinatarios: r.destinatarios.join(', '), alertaInternoObrigatorio: r.alertaInternoObrigatorio, ativo: r.ativo,
  });

  const salvarRegra = () => {
    setSalvandoRegra(true);
    maestroService.salvarRegraNotificacao({
      eventType: editandoRegra.eventType,
      canalMaestro: editandoRegra.canalMaestro,
      canalEmail: editandoRegra.canalEmail,
      canalWhatsapp: editandoRegra.canalWhatsapp,
      antecedenciaDiasUteis: editandoRegra.antecedenciaDiasUteis ? Number(editandoRegra.antecedenciaDiasUteis) : undefined,
      destinatarios: editandoRegra.destinatarios.split(',').map(s => s.trim()).filter(Boolean),
      alertaInternoObrigatorio: editandoRegra.alertaInternoObrigatorio,
      ativo: editandoRegra.ativo,
    })
      .then(regra => setRegras(prev => {
        const outras = prev.filter(r => r.eventType !== regra.eventType);
        return [...outras, regra].sort((a, b) => a.eventType.localeCompare(b.eventType));
      }))
      .catch(() => dispararAcaoSimulada('Não foi possível salvar a regra de notificação.'))
      .finally(() => setSalvandoRegra(false));
  };

  const criarTemplate = () => {
    if (!novoTemplate.corpo.trim()) return;
    maestroService.criarTemplateNotificacao({ eventType: eventoTemplate, canal: canalTemplate, assunto: novoTemplate.assunto.trim() || undefined, corpo: novoTemplate.corpo.trim() })
      .then(t => { setTemplates(prev => [t, ...prev]); setMostrarNovoTemplate(false); setNovoTemplate({ assunto: '', corpo: '' }); })
      .catch(() => dispararAcaoSimulada('Não foi possível criar o template.'));
  };

  const publicarTemplate = (id: number) => {
    maestroService.publicarTemplateNotificacao(id)
      .then(t => setTemplates(prev => prev.map(item => (item.id === id ? t : item))))
      .catch(() => dispararAcaoSimulada('Não foi possível publicar o template.'));
  };

  const salvarCanais = () => {
    setSalvandoCanais(true);
    maestroService.atualizarConfiguracaoCanais(formCanais)
      .then(setCanais)
      .catch(() => dispararAcaoSimulada('Não foi possível salvar a configuração de canais.'))
      .finally(() => setSalvandoCanais(false));
  };

  const enviarTeste = () => {
    if (!teste.destinatarioUsuarioId) return;
    setEnviandoTeste(true);
    setResultadoTeste(null);
    maestroService.testarNotificacao({ eventType: teste.eventType, canal: teste.canal, destinatarioUsuarioId: Number(teste.destinatarioUsuarioId) })
      .then(setResultadoTeste)
      .catch(() => dispararAcaoSimulada('Não foi possível enviar a notificação de teste.'))
      .finally(() => setEnviandoTeste(false));
  };

  if (carregando) return <div className='mc-page'><Header title='Central de Notificações' /><SkeletonCard /></div>;
  if (erro) return <div className='mc-page'><Header title='Central de Notificações' /><p className='mc-texto-danger'>{erro}</p></div>;

  return (
    <div className='mc-page'>
      <Header
        title='Central de Notificações'
        subtitle='Regras por evento, templates versionados, canais (e-mail/WhatsApp) e a fila real de entregas do módulo.'
        actions={
          <div className='mc-tabs'>
            <button type='button' className={`mc-btn mc-btn--sm ${aba === 'REGRAS' ? 'mc-btn--primary' : 'mc-btn--secondary'}`} onClick={() => setAba('REGRAS')}>Regras</button>
            <button type='button' className={`mc-btn mc-btn--sm ${aba === 'TEMPLATES' ? 'mc-btn--primary' : 'mc-btn--secondary'}`} onClick={() => setAba('TEMPLATES')}>Templates</button>
            <button type='button' className={`mc-btn mc-btn--sm ${aba === 'CANAIS' ? 'mc-btn--primary' : 'mc-btn--secondary'}`} onClick={() => setAba('CANAIS')}>Canais</button>
            <button type='button' className={`mc-btn mc-btn--sm ${aba === 'TESTAR' ? 'mc-btn--primary' : 'mc-btn--secondary'}`} onClick={() => setAba('TESTAR')}>Testar envio</button>
            <button type='button' className={`mc-btn mc-btn--sm ${aba === 'ENTREGAS' ? 'mc-btn--primary' : 'mc-btn--secondary'}`} onClick={() => setAba('ENTREGAS')}>Fila de entregas</button>
          </div>
        }
      />

      {aba === 'REGRAS' && (
        <>
          <Card title='Regras cadastradas' subtitle={`${regras.length} de ${EVENTOS_NOTIFICACAO.length} eventos configurados`}>
            {regras.length === 0
              ? <EstadoVazio titulo='Nenhuma regra configurada ainda' descricao='Use o formulário abaixo para configurar o primeiro evento.' />
              : (
                <table className='mc-logs-table'>
                  <thead><tr><th>Evento</th><th>Canais</th><th>Antecedência</th><th>Destinatários</th><th>Status</th><th /></tr></thead>
                  <tbody>
                    {regras.map(r => (
                      <tr key={r.id}>
                        <td>{labelEvento(r.eventType)}</td>
                        <td>{[r.canalMaestro && 'Maestro', r.canalEmail && 'E-mail', r.canalWhatsapp && 'WhatsApp'].filter(Boolean).join(', ') || '—'}</td>
                        <td>{r.antecedenciaDiasUteis != null ? `${r.antecedenciaDiasUteis} dia(s) útil(eis)` : '—'}</td>
                        <td>{r.destinatarios.length > 0 ? r.destinatarios.join(', ') : '—'}</td>
                        <td><span className={`mc-badge mc-badge--${r.ativo ? 'success' : 'neutro'}`}>{r.ativo ? 'Ativa' : 'Inativa'}</span></td>
                        <td><button type='button' className='mc-btn mc-btn--secondary mc-btn--sm' onClick={() => editarRegraExistente(r)}>Editar</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
          </Card>

          <Card title='Configurar regra' subtitle='Selecione o evento e defina canais, antecedência e destinatários.'>
            <div className='mc-form-inline'>
              <label>Evento
                <select value={editandoRegra.eventType} onChange={e => setEditandoRegra(regraVazia(e.target.value))}>
                  {EVENTOS_NOTIFICACAO.map(ev => <option key={ev.valor} value={ev.valor}>{ev.label}</option>)}
                </select>
              </label>
              <label>Antecedência (dias úteis)
                <input type='number' min={0} value={editandoRegra.antecedenciaDiasUteis} onChange={e => setEditandoRegra({ ...editandoRegra, antecedenciaDiasUteis: e.target.value })} />
              </label>
              <label>Destinatários adicionais (e-mails, separados por vírgula)
                <input type='text' value={editandoRegra.destinatarios} onChange={e => setEditandoRegra({ ...editandoRegra, destinatarios: e.target.value })} />
              </label>
            </div>
            <div className='mc-checkbox-grupo'>
              <label className='mc-checkbox'><input type='checkbox' checked={editandoRegra.canalMaestro} onChange={e => setEditandoRegra({ ...editandoRegra, canalMaestro: e.target.checked })} /> Canal Maestro</label>
              <label className='mc-checkbox'><input type='checkbox' checked={editandoRegra.canalEmail} onChange={e => setEditandoRegra({ ...editandoRegra, canalEmail: e.target.checked })} /> Canal E-mail</label>
              <label className='mc-checkbox'><input type='checkbox' checked={editandoRegra.canalWhatsapp} onChange={e => setEditandoRegra({ ...editandoRegra, canalWhatsapp: e.target.checked })} /> Canal WhatsApp</label>
              <label className='mc-checkbox'><input type='checkbox' checked={editandoRegra.alertaInternoObrigatorio} onChange={e => setEditandoRegra({ ...editandoRegra, alertaInternoObrigatorio: e.target.checked })} /> Alerta interno obrigatório</label>
              <label className='mc-checkbox'><input type='checkbox' checked={editandoRegra.ativo} onChange={e => setEditandoRegra({ ...editandoRegra, ativo: e.target.checked })} /> Regra ativa</label>
            </div>
            <button type='button' className='mc-btn mc-btn--primary' disabled={salvandoRegra} onClick={salvarRegra}>
              {salvandoRegra ? 'Salvando...' : 'Salvar regra'}
            </button>
          </Card>
        </>
      )}

      {aba === 'TEMPLATES' && (
        <Card
          title='Templates por evento e canal'
          subtitle='Versionados — uma vez publicado, o template anterior fica preservado no histórico.'
          actions={<button type='button' className='mc-btn mc-btn--primary mc-btn--sm' onClick={() => setMostrarNovoTemplate(v => !v)}>{mostrarNovoTemplate ? 'Cancelar' : 'Novo template'}</button>}
        >
          <div className='mc-form-inline'>
            <label>Evento
              <select value={eventoTemplate} onChange={e => setEventoTemplate(e.target.value)}>
                {EVENTOS_NOTIFICACAO.map(ev => <option key={ev.valor} value={ev.valor}>{ev.label}</option>)}
              </select>
            </label>
            <label>Canal
              <select value={canalTemplate} onChange={e => setCanalTemplate(e.target.value)}>
                {CANAIS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
          </div>

          {mostrarNovoTemplate && (
            <div className='mc-form-inline'>
              <label style={{ gridColumn: '1 / -1' }}>Assunto (opcional, para e-mail)
                <input type='text' value={novoTemplate.assunto} onChange={e => setNovoTemplate({ ...novoTemplate, assunto: e.target.value })} />
              </label>
              <label style={{ gridColumn: '1 / -1' }}>Corpo
                <input type='text' placeholder='Use {{PLACEHOLDER}} para variáveis dinâmicas' value={novoTemplate.corpo} onChange={e => setNovoTemplate({ ...novoTemplate, corpo: e.target.value })} />
              </label>
              <button type='button' className='mc-btn mc-btn--primary mc-btn--sm' disabled={!novoTemplate.corpo.trim()} onClick={criarTemplate}>Criar rascunho</button>
            </div>
          )}

          {carregandoTemplates
            ? <SkeletonCard />
            : templates.length === 0
              ? <EstadoVazio titulo='Nenhum template para este evento/canal' />
              : (
                <table className='mc-logs-table'>
                  <thead><tr><th>Versão</th><th>Status</th><th>Assunto</th><th>Corpo</th><th>Criado em</th><th /></tr></thead>
                  <tbody>
                    {templates.map(t => (
                      <tr key={t.id}>
                        <td>v{t.versao}</td>
                        <td><span className={`mc-badge mc-badge--${t.status === 'PUBLICADO' ? 'success' : 'neutro'}`}>{t.status}</span></td>
                        <td>{t.assunto ?? '—'}</td>
                        <td>{t.corpo}</td>
                        <td>{formatarData(t.criadoEm)}</td>
                        <td>{t.status !== 'PUBLICADO' && <button type='button' className='mc-btn mc-btn--secondary mc-btn--sm' onClick={() => publicarTemplate(t.id)}>Publicar</button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
        </Card>
      )}

      {aba === 'CANAIS' && (
        <Card title='Configuração de canais' subtitle='E-mail e WhatsApp Business — dados reais de configuração do canal, sem envio simulado.'>
          <div className='mc-form-inline'>
            <label>Status do WhatsApp
              <select value={formCanais.whatsappStatus} onChange={e => setFormCanais({ ...formCanais, whatsappStatus: e.target.value })}>
                <option value='DESABILITADO'>Desabilitado</option>
                <option value='PENDENTE_CONFIGURACAO_WHATSAPP'>Pendente de configuração</option>
                <option value='ATIVO'>Ativo (exige número remetente e token configurados)</option>
                <option value='ERRO_INTEGRACAO'>Erro de integração</option>
              </select>
            </label>
            <label>Número remetente (WhatsApp)
              <input type='text' placeholder='+55...' value={formCanais.whatsappNumeroRemetente} onChange={e => setFormCanais({ ...formCanais, whatsappNumeroRemetente: e.target.value })} />
            </label>
          </div>
          <div className='mc-checkbox-grupo'>
            <label className='mc-checkbox'><input type='checkbox' checked={formCanais.emailHabilitado} onChange={e => setFormCanais({ ...formCanais, emailHabilitado: e.target.checked })} /> Canal e-mail habilitado</label>
            <label className='mc-checkbox'><input type='checkbox' checked={formCanais.whatsappTokenConfigurado} onChange={e => setFormCanais({ ...formCanais, whatsappTokenConfigurado: e.target.checked })} /> Token da API do WhatsApp Business configurado</label>
          </div>
          <button type='button' className='mc-btn mc-btn--primary' disabled={salvandoCanais} onClick={salvarCanais}>{salvandoCanais ? 'Salvando...' : 'Salvar configuração'}</button>
          {canais?.atualizadoEm && <p className='mc-texto-neutro' style={{ marginTop: 10 }}>Última atualização em {formatarData(canais.atualizadoEm)}.</p>}
        </Card>
      )}

      {aba === 'TESTAR' && (
        <Card title='Enviar notificação de teste' subtitle='Dispara um envio real (respeitando o canal habilitado) para o destinatário selecionado.'>
          <div className='mc-form-inline'>
            <label>Evento
              <select value={teste.eventType} onChange={e => setTeste({ ...teste, eventType: e.target.value })}>
                {EVENTOS_NOTIFICACAO.map(ev => <option key={ev.valor} value={ev.valor}>{ev.label}</option>)}
              </select>
            </label>
            <label>Canal
              <select value={teste.canal} onChange={e => setTeste({ ...teste, canal: e.target.value })}>
                {CANAIS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label>Destinatário
              <select value={teste.destinatarioUsuarioId} onChange={e => setTeste({ ...teste, destinatarioUsuarioId: e.target.value })}>
                <option value=''>Selecione...</option>
                {usuarios.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
              </select>
            </label>
            <button type='button' className='mc-btn mc-btn--primary' disabled={!teste.destinatarioUsuarioId || enviandoTeste} onClick={enviarTeste}>{enviandoTeste ? 'Enviando...' : 'Enviar teste'}</button>
          </div>

          {resultadoTeste && (
            resultadoTeste.length === 0
              ? <p className='mc-texto-neutro'>Nenhuma entrega gerada — confira se há regra ativa e template publicado para este evento/canal.</p>
              : (
                <table className='mc-logs-table'>
                  <thead><tr><th>Canal</th><th>Status</th><th>Mensagem</th><th>Erro</th></tr></thead>
                  <tbody>
                    {resultadoTeste.map(e => (
                      <tr key={e.id}>
                        <td>{e.canal}</td>
                        <td><span className={`mc-badge mc-badge--${e.status === 'ENVIADA' ? 'success' : e.status === 'FALHA' ? 'danger' : 'warning'}`}>{e.status}</span></td>
                        <td>{e.mensagem}</td>
                        <td>{e.erro ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
          )}
        </Card>
      )}

      {aba === 'ENTREGAS' && (
        <Card title={`${entregas.length} entrega(s)`} subtitle={cicloAtivo.id ? `Ciclo ativo: ${cicloAtivo.competencia}` : 'Sem ciclo ativo selecionado — mostrando entregas de todos os ciclos.'}>
          <div className='mc-form-inline'>
            <label>Status
              <select value={statusEntrega} onChange={e => setStatusEntrega(e.target.value)}>
                <option value=''>Todos</option>
                {STATUS_ENTREGA.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
          </div>

          {carregandoEntregas
            ? <SkeletonCard />
            : entregas.length === 0
              ? <EstadoVazio titulo='Nenhuma entrega encontrada' />
              : (
                <table className='mc-logs-table'>
                  <thead><tr><th>Data/Hora</th><th>Evento</th><th>Canal</th><th>Destinatário</th><th>Tentativa</th><th>Status</th><th>Erro</th></tr></thead>
                  <tbody>
                    {entregas.map(e => {
                      const destinatario = usuarios.find(u => u.id === e.destinatarioUsuarioId);
                      return (
                        <tr key={e.id}>
                          <td>{formatarData(e.criadoEm)}</td>
                          <td>{labelEvento(e.eventType)}{e.fallback ? ' (fallback)' : ''}</td>
                          <td>{e.canal}</td>
                          <td>{destinatario?.nome ?? `#${e.destinatarioUsuarioId}`}</td>
                          <td>{e.tentativaNumero}</td>
                          <td><span className={`mc-badge mc-badge--${e.status === 'ENVIADA' ? 'success' : e.status === 'FALHA' ? 'danger' : 'warning'}`}>{e.status}</span></td>
                          <td>{e.erro ?? '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
        </Card>
      )}
    </div>
  );
};

export default MaestroNotificacoesPage;
